import { Currency } from '@seapoint/server/enums';
import { format } from 'date-fns';
import { Area, AreaChart, CartesianGrid, ReferenceDot, ReferenceLine, XAxis, YAxis } from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip } from '../../../../design-system/chart/chart';
import { cn } from '../../../../utils/cn';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { getCurrencySymbol } from '../../../../utils/get-currency-symbol';
import { RunwayDataPoint } from '../../utils/get-runway-chart-data';

type EstimatedRunwayChartProps = {
  height: number;
  data: RunwayDataPoint[];
  baseCurrency: Currency;
  isGrowing?: boolean;
};

const chartConfig = {
  balance: {
    label: 'Balance',
    color: '#FFE222',
  },
} satisfies ChartConfig;

export const EstimatedRunwayChart = ({ height, data, baseCurrency }: EstimatedRunwayChartProps) => {
  const strokeColor = '#FFE222';
  const fillColorStart = '#FFE222';

  const todayDataPoint = data.find((point) => point.period === 'Today');

  return (
    <ChartContainer config={chartConfig} className={cn(`max-h-[${height}px]`, 'w-full')}>
      <AreaChart accessibilityLayer data={data} className="p-2">
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="period"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          interval={'preserveEnd'}
          height={30}
          ticks={[
            ...data.map((point) => point.period),
            ...(todayDataPoint ? [format(new Date(todayDataPoint.date), 'MMM d')] : []),
          ]}
          tickFormatter={(value) => {
            if (value === 'Today' && todayDataPoint) {
              return 'Today';
            }
            return value;
          }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickCount={5}
          domain={[0, 'dataMax']}
          tickFormatter={(value) => {
            const numValue = value as number;
            const symbol = getCurrencySymbol(baseCurrency);
            if (Math.abs(numValue) >= 1000000) {
              return `${symbol}${(numValue / 1000000).toFixed(1)}M`;
            } else if (Math.abs(numValue) >= 1000) {
              return `${symbol}${(numValue / 1000).toFixed(0)}K`;
            } else {
              return getAmountWithCurrency({
                amount: numValue,
                currency: baseCurrency,
                isAccountBalance: true,
              });
            }
          }}
        />
        <ChartTooltip
          cursor={false}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length > 0) {
              const dataPoint = payload[0].payload as RunwayDataPoint;
              const balance = dataPoint.balance;
              const burnAmount = dataPoint.burnAmount;
              const runwayMonths = dataPoint.runwayMonths;
              const displayLabel = dataPoint.date ? format(new Date(dataPoint.date), 'MMMM d, yyyy') : label;

              const formattedBalance = getAmountWithCurrency({
                amount: parseFloat(balance.toFixed(2)),
                currency: baseCurrency,
                isAccountBalance: true,
              });

              const formattedBurn = burnAmount
                ? getAmountWithCurrency({
                    amount: parseFloat(burnAmount.toFixed(2)),
                    currency: baseCurrency,
                    isAccountBalance: true,
                  })
                : '—';

              const formattedRunway = runwayMonths ? `${runwayMonths.toFixed(1)} months` : '—';

              return (
                <div className="rounded-lg border bg-white p-2 shadow-md">
                  <div className="grid gap-2">
                    <div className="font-medium">{displayLabel}</div>
                    <div className="grid gap-1 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">Account Balance:</span>
                        <span className="font-medium">{formattedBalance}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">Last full month burn:</span>
                        <span className="font-medium">{formattedBurn}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">Runway:</span>
                        <span className="font-medium">{formattedRunway}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />
        <defs>
          <linearGradient id="fillEstimatedRunway" x1="0" y1="0" x2="0" y2="1">
            <stop offset="20%" stopColor={fillColorStart} stopOpacity={0.8} />
            <stop offset="95%" stopColor="#ffffff" stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <Area
          dataKey="balance"
          type="monotone"
          fill="url(#fillEstimatedRunway)"
          fillOpacity={0.4}
          stroke={strokeColor}
          strokeWidth={2}
          stackId="a"
        />
        {todayDataPoint && (
          <>
            <ReferenceLine
              segment={[
                { x: 'Today', y: 0 },
                { x: 'Today', y: todayDataPoint.balance },
              ]}
              stroke="#000000"
              strokeWidth={1}
              strokeDasharray="none"
            />
            <ReferenceDot x="Today" y={0} r={3} fill="#000000" stroke="#ffffff" strokeWidth={1} />
          </>
        )}
      </AreaChart>
    </ChartContainer>
  );
};
